// ============================================================
// COMPARATIVE ANALYTICS — Fase 8.3
// Tendências corporativas, evolução semanal, alertas escalados
// ============================================================
import type { ObraSnapshot, CorporateSnapshot, ExecutiveAlert } from '@/types/corporate'
import type { ObraRanking } from '@/types/corporate'

// ─── Corporate trend ─────────────────────────────────────────
export interface CorporateTrend {
  label:     string
  direction: 'improving' | 'stable' | 'worsening'
  pct:       number   // magnitude of change
  detail:    string
}

export function analyzeCorporateTrend(
  snapshot: CorporateSnapshot,
  obras:    ObraSnapshot[],
): CorporateTrend[] {
  const trends: CorporateTrend[] = []

  // Obras críticas trend
  const obrasHighRisk = obras.filter(o => o.riskLevel === 'critical' || o.riskLevel === 'high').length
  const critPct = obras.length > 0 ? Math.round((obrasHighRisk / obras.length) * 100) : 0

  trends.push({
    label:     'Obras em risco',
    direction: critPct > 30 ? 'worsening' : critPct < 10 ? 'improving' : 'stable',
    pct:       critPct,
    detail:    `${obrasHighRisk} de ${obras.length} obras com risco elevado`,
  })

  // Supply health
  const totalSupplyCrit = obras.reduce((s, o) => s + o.supply_criticos, 0)
  trends.push({
    label:     'Suprimentos',
    direction: totalSupplyCrit > 5 ? 'worsening' : totalSupplyCrit === 0 ? 'improving' : 'stable',
    pct:       Math.min(100, totalSupplyCrit * 10),
    detail:    `${totalSupplyCrit} material(is) crítico(s) em toda a empresa`,
  })

  // SST
  const totalIncidents = obras.reduce((s, o) => s + o.incidentes_ativos, 0)
  const totalAPR       = obras.reduce((s, o) => s + o.aprs_vencidas, 0)
  trends.push({
    label:     'Segurança SST',
    direction: (totalIncidents + totalAPR) > 5 ? 'worsening' : (totalIncidents + totalAPR) === 0 ? 'improving' : 'stable',
    pct:       Math.min(100, (totalIncidents * 15) + (totalAPR * 5)),
    detail:    `${totalIncidents} incidente(s), ${totalAPR} APR(s) vencida(s)`,
  })

  return trends
}

// ─── Alert escalation rules ──────────────────────────────────
export function checkEscalationRules(
  obras:     ObraSnapshot[],
  companyId: string,
): Omit<ExecutiveAlert, 'id' | 'created_at'>[] {
  const alerts: Omit<ExecutiveAlert, 'id' | 'created_at'>[] = []

  const criticalObras = obras.filter(o => o.riskLevel === 'critical')
  const highRiskObras = obras.filter(o => o.riskLevel === 'critical' || o.riskLevel === 'high')

  // Rule: 3+ obras críticas → corporate alert
  if (criticalObras.length >= 3) {
    alerts.push({
      company_id:     companyId,
      obra_id:        null,
      tipo:           'corporate',
      severity:       'emergency',
      title:          `🚨 ${criticalObras.length} obras em estado CRÍTICO`,
      description:    `Ação executiva imediata necessária: ${criticalObras.map(o => o.nome).join(', ')}`,
      obras_afetadas: criticalObras.map(o => o.id),
      source:         'escalation_engine',
      payload:        { count: criticalObras.length },
      is_resolved:    false,
      resolved_at:    null,
      expires_at:     new Date(Date.now() + 4 * 3600_000).toISOString(),
    })
  }

  // Rule: supply failure across 2+ obras
  const supplyFailures = obras.filter(o => o.supply_criticos >= 2)
  if (supplyFailures.length >= 2) {
    alerts.push({
      company_id:     companyId,
      obra_id:        null,
      tipo:           'supply_chain',
      severity:       'critical',
      title:          `📦 Ruptura de supply em ${supplyFailures.length} obras`,
      description:    `Materiais críticos em risco: ${supplyFailures.map(o => o.nome).join(', ')}`,
      obras_afetadas: supplyFailures.map(o => o.id),
      source:         'supply_escalation',
      payload:        { obras: supplyFailures.map(o => o.id) },
      is_resolved:    false,
      resolved_at:    null,
      expires_at:     new Date(Date.now() + 8 * 3600_000).toISOString(),
    })
  }

  // Rule: SST critical across multiple obras
  const sstCritical = obras.filter(o => o.incidentes_ativos > 0)
  if (sstCritical.length >= 2) {
    alerts.push({
      company_id:     companyId,
      obra_id:        null,
      tipo:           'sst_critical',
      severity:       'critical',
      title:          `🦺 Incidentes ativos em ${sstCritical.length} obras`,
      description:    `Investigação e DDS emergencial recomendados`,
      obras_afetadas: sstCritical.map(o => o.id),
      source:         'sst_escalation',
      payload:        { total_incidents: obras.reduce((s, o) => s + o.incidentes_ativos, 0) },
      is_resolved:    false,
      resolved_at:    null,
      expires_at:     new Date(Date.now() + 24 * 3600_000).toISOString(),
    })
  }

  return alerts
}

// ─── Top/Bottom performers ────────────────────────────────────
export interface PerformanceSegment {
  top3:    ObraSnapshot[]
  bottom3: ObraSnapshot[]
  average: number
}

export function segmentPerformance(obras: ObraSnapshot[]): PerformanceSegment {
  const sorted = [...obras].sort((a, b) => (b.riskScore ?? 50) - (a.riskScore ?? 50))
  // Note: higher riskScore = WORSE performance here, so invert
  const byPerf = [...obras].sort((a, b) => (b.riskScore ?? 50) - (a.riskScore ?? 50))
  // best performance = lowest risk score
  const byPerfAsc = [...obras].sort((a, b) => (a.riskScore ?? 50) - (b.riskScore ?? 50))

  const avg = obras.length
    ? Math.round(obras.reduce((s, o) => s + (o.riskScore ?? 50), 0) / obras.length)
    : 50

  return {
    top3:    byPerfAsc.slice(0, 3),
    bottom3: byPerf.slice(0, 3),
    average: avg,
  }
}
